import React, { useCallback, useEffect, useRef, useState } from 'react';
import { captureAPI } from '../services/api';
import './PublishStatusNotice.css';

/**
 * The document's publish status on the Review detail page.
 *
 * `publishing` shows a quiet line. `failed` shows the latest failure from the
 * publish history (destination and reason) and offers "Retry publish", which
 * calls the same manual publish route with the destination the failed attempt
 * used. The server's already-published check runs on a retry as on any
 * publish, so a document that did reach the Library is recognised and not sent
 * a second time. `published` and `unpublished` render nothing: the page's own
 * Published badge covers the first, and the second needs no remark.
 *
 * Who may retry is whoever may publish: the manual publish route is open to
 * every member of the tenant, and the server is what enforces that.
 *
 * HOW the retry is sent is the page's decision, through `retry`: a document
 * that belongs to a workflow run is retried on that run (its Publish node, with
 * the node's field selection and renames), never through the manual route.
 * Without `retry` the manual route is used, with the failed attempt's
 * destination. `retryWithoutAttempt` offers Retry when no failed attempt is on
 * record (a run that failed at its Publish node before any log row was written).
 *
 * `fallbackReason` and `reference` come from an Approve that has just failed at
 * publishing: the reason the server gave, used when the history has no failed
 * attempt to read one from, and the error id of that failure. With them the
 * notice is the ONE place the failure is shown; the page no longer repeats it
 * in its inline error.
 */

// The fixed reasons capture-service writes to the publish log, in a
// reviewer's words. Any other text is already authored (plus an error id) and
// is shown as it is.
const REASON_TEXT = {
  'destination inactive': 'The destination is inactive. Reactivate it in Destinations, then retry.',
  'destination not found': 'The destination no longer exists.',
  'destination deleted': 'This destination was deleted. Choose another destination in the workflow, then retry.',
  interrupted:
    'The publish was interrupted before it finished, so it is not known whether the document arrived. Check the destination before retrying.',
};

export function latestFailure(history) {
  const rows = Array.isArray(history) ? history : [];
  // Newest first. Only the newest attempt counts: an older failure followed by
  // something else is not why the document is failed now.
  return rows.length > 0 && rows[0].status === 'failed' ? rows[0] : null;
}

export default function PublishStatusNotice({
  captureDocumentId,
  publishStatus,
  onPublished,
  retry,
  retryWithoutAttempt = false,
  fallbackReason = null,
  reference = null,
}) {
  const [failure, setFailure] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState(null);
  // Single-flight. A ref, not the `retrying` state: two clicks in one tick both
  // see the state as it was before either of them.
  const inFlight = useRef(false);

  const loadHistory = useCallback(async () => {
    try {
      const res = await captureAPI.publish.getHistory(captureDocumentId);
      setFailure(latestFailure(res?.history));
    } catch {
      setFailure(null);
    } finally {
      setLoaded(true);
    }
  }, [captureDocumentId]);

  useEffect(() => {
    setFailure(null);
    setLoaded(false);
    setRetryError(null);
    if (publishStatus === 'failed' && captureDocumentId) loadHistory();
  }, [publishStatus, captureDocumentId, loadHistory]);

  const canRetry = Boolean(failure?.destination_id) || (retryWithoutAttempt && loaded);

  const handleRetry = useCallback(async () => {
    if (!canRetry || inFlight.current) return;
    inFlight.current = true;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = retry
        ? await retry(failure)
        : await captureAPI.publish.publishDocument(captureDocumentId, {
            destinationId: failure.destination_id,
          });
      if (!res?.ok) {
        // A run retry answers 200 ok:false with the fixed reason and its id.
        const text = res?.error || 'Publish failed';
        throw new Error(res?.error_id ? `${text} (error_id: ${res.error_id})` : text);
      }
      onPublished?.(failure?.destination_name || null);
    } catch (err) {
      const body = err?.response?.data;
      const text = body?.error || err?.message || 'Publish failed';
      setRetryError(body?.error_id ? `${text} (error_id: ${body.error_id})` : text);
      // The failed retry wrote its own history row; show that one now.
      loadHistory();
    } finally {
      inFlight.current = false;
      setRetrying(false);
    }
  }, [canRetry, failure, retry, captureDocumentId, onPublished, loadHistory]);

  if (publishStatus === 'publishing') {
    return (
      <div className="psn psn--publishing" role="status">
        Publish status: publishing…
      </div>
    );
  }
  if (publishStatus !== 'failed') return null;

  const destination = failure?.destination_name || (failure ? 'a destination that no longer exists' : null);
  const reason = failure
    ? REASON_TEXT[failure.error_message] || failure.error_message || 'No reason was recorded.'
    // No attempt on record, but the page was just told why (the Approve that
    // failed): that reason, in place of the general line.
    : loaded && fallbackReason
      ? fallbackReason
    : loaded && retryWithoutAttempt
      ? 'The workflow run failed at its Publish step before an attempt was recorded.'
    : loaded
      ? 'No failed attempt is recorded for this document, so there is no destination to retry. Publish it again from its workflow, or approve it to publish to the default destination.'
      : 'Loading the reason…';

  return (
    <div className="psn psn--failed" role="alert">
      <div className="psn-text">
        <strong>Publish failed{destination ? ` · ${destination}` : ''}</strong>
        <span className="psn-reason">{reason}</span>
        {/* The reference of the failure the page was just told about, unless
            the reason already carries it. */}
        {reference && !String(reason).includes(reference) && (
          <span className="psn-reference">Reference: {reference}</span>
        )}
        {retryError && <span className="psn-retry-error">Retry failed: {retryError}</span>}
      </div>
      {canRetry && (
        <button type="button" className="psn-retry" onClick={handleRetry} disabled={retrying}>
          {retrying ? 'Retrying…' : 'Retry publish'}
        </button>
      )}
    </div>
  );
}
